import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BedDouble, Bath, Maximize, MapPin, ArrowLeft, Check } from "lucide-react";
import { api, fmtPrice, fmtBaths } from "../lib/api";
import Nav from "../components/site/Nav";
import Footer from "../components/site/Footer";
import Contact from "../components/site/Contact";
import MortgageCalculator from "../components/site/MortgageCalculator";
import PropertyMap from "../components/site/PropertyMap";
import VideoTour from "../components/site/VideoTour";
import FavoriteButton from "../components/site/FavoriteButton";
import ShareButton from "../components/site/ShareButton";

export default function PropertyDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [profile, setProfile] = useState(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/properties/${id}`).then((r) => { setP(r.data); setActive(0); }).catch(() => setP(false));
    api.get("/profile").then((r) => setProfile(r.data));
  }, [id]);

  if (p === false) return (
    <div className="min-h-screen grid place-items-center bg-[#FAF9F6]">
      <div className="text-center">
        <p className="font-serif text-3xl text-[#0A1128] mb-4">Property not found</p>
        <Link to="/" className="text-[var(--gold)] link-underline">Return home</Link>
      </div>
    </div>
  );
  if (!p) return <div className="min-h-screen bg-[#FAF9F6]" />;

  return (
    <div className="bg-[#FAF9F6]">
      <Nav name={profile?.name} logo={profile?.logo} />
      <div className="pt-28 pb-12 max-w-7xl mx-auto px-5 md:px-8 flex items-center justify-between">
        <Link to="/" data-testid="back-link" className="inline-flex items-center gap-2 text-sm text-[var(--slate)] hover:text-[#0A1128] transition-colors">
          <ArrowLeft size={16} /> Back to Collection
        </Link>
        <ShareButton title={p.title} />
      </div>

      <motion.div initial={{ clipPath: "inset(100% 0 0 0)" }} animate={{ clipPath: "inset(0 0 0 0)" }}
        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
        className="max-w-7xl mx-auto px-5 md:px-8 relative">
        <img data-testid="gallery-main" src={p.images?.[active]} alt={p.title} className="w-full h-[70vh] object-cover" />
        <FavoriteButton id={p.id} light className="absolute top-4 right-9 md:right-12" />
      </motion.div>

      {p.images?.length > 1 && (
        <div className="max-w-7xl mx-auto px-5 md:px-8 mt-4 grid grid-cols-4 gap-4">
          {p.images.map((img, i) => (
            <button key={i} data-testid={`gallery-thumb-${i}`} onClick={() => setActive(i)}
              className={`hover-img h-24 md:h-32 ${active === i ? "ring-2 ring-[var(--gold)]" : ""}`}>
              <img src={img} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}

      <div className="max-w-7xl mx-auto px-5 md:px-8 py-20 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-7">
          <span className="text-xs uppercase tracking-[0.2em] text-[var(--gold)]">{p.property_type} · {p.status}{p.mls ? ` · MLS# ${p.mls}` : ""}</span>
          <h1 className="font-serif text-4xl md:text-6xl tracking-tight text-[#0A1128] mt-4">{p.title}</h1>
          <p className="flex items-center gap-2 text-[var(--slate)] mt-3"><MapPin size={16} /> {p.address || p.location}</p>

          <div className="flex gap-8 mt-8 py-6 border-y border-[#0A1128]/10">
            <span className="flex items-center gap-2 text-[#0A1128]"><BedDouble size={18} /> {p.beds} Beds</span>
            <span className="flex items-center gap-2 text-[#0A1128]"><Bath size={18} /> {fmtBaths(p.baths)} Baths</span>
            {p.area > 0 && <span className="flex items-center gap-2 text-[#0A1128]"><Maximize size={18} /> {p.area?.toLocaleString()} sqft</span>}
          </div>

          <h2 className="font-serif text-2xl text-[#0A1128] mt-12 mb-4">The Residence</h2>
          <p className="text-[var(--slate)] leading-relaxed text-[17px]">{p.description}</p>

          {p.features?.length > 0 && (
            <>
              <h2 className="font-serif text-2xl text-[#0A1128] mt-12 mb-6">Key Features</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {p.features.map((f) => (
                  <div key={f} className="flex items-center gap-3 text-[var(--slate)]">
                    <Check size={16} className="text-[var(--gold)]" /> {f}
                  </div>
                ))}
              </div>
            </>
          )}

          {p.price > 0 && <MortgageCalculator price={p.price} currency={p.currency} />}
          <VideoTour url={p.video_url} title={p.title} />
          {(p.video_url) && (
            <div data-testid="showing-cta" className="mt-6 border border-[var(--gold)]/60 bg-[var(--gold)]/[0.08] p-8 md:flex items-center justify-between gap-6">
              <div>
                <h3 className="font-serif text-2xl text-[#0A1128]">Ready to see it in person?</h3>
                <p className="text-[var(--slate)] text-sm mt-1">A tour is a great start — a walk-through seals the feeling.</p>
              </div>
              <button
                data-testid="request-showing-btn"
                onClick={() => document.getElementById("showing-form")?.scrollIntoView({ behavior: "smooth", block: "center" })}
                className="mt-4 md:mt-0 shrink-0 px-8 py-4 bg-[#0A1128] text-[#FAF9F6] text-sm uppercase tracking-[0.14em] hover:bg-[#1a2342] transition-colors duration-300"
              >
                Request a Private Showing
              </button>
            </div>
          )}
          <PropertyMap lat={p.lat} lng={p.lng} title={p.title} />
        </div>

        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28 border border-[#0A1128]/12 p-8 bg-white">
            <div className="text-xs uppercase tracking-[0.2em] text-[var(--slate)]">Offered At</div>
            <div className="font-serif text-4xl text-[var(--gold)] mt-2 mb-6">{fmtPrice(p.price, p.currency)}</div>
            <div id="showing-form" className="border-t border-[#0A1128]/10 pt-6">
              <h3 className="font-serif text-2xl text-[#0A1128] mb-1">Request a Private Showing</h3>
              <p className="text-[var(--slate)] text-sm mb-6">Reach out and I&apos;ll respond personally.</p>
              <Contact compact propertyId={p.id} propertyTitle={p.title} />
            </div>
          </div>
        </div>
      </div>
      <Footer profile={profile} />
    </div>
  );
}
